import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

// mailroom's msgs_msg.direction: "I" incoming (from the customer), "O" outgoing (to the customer)
function Bubble({ message }) {
  const isIncoming = message.direction === 'I';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isIncoming ? 'flex-start' : 'flex-end',
        marginBottom: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: '70%',
          padding: '8px 12px',
          borderRadius: 2,
          backgroundColor: isIncoming ? '#e9e9eb' : '#0b84ff',
          color: isIncoming ? 'text.primary' : '#fff',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {message.text}
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, display: 'block', marginTop: 0.5 }}
        >
          {new Date(message.created_on).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
}

// Mirrors SessionsList's SESSION_STATUS - kept separate rather than shared since the label here reads as
// a transcript event ("Session interrupted") rather than a list-row state ("Interrupted").
const SESSION_STATUS = {
  W: { label: 'Session waiting', color: '#2563eb' },
  C: { label: 'Session completed', color: '#16a34a' },
  X: { label: 'Session expired', color: '#9ca3af' },
  I: { label: 'Session interrupted', color: '#d97706' },
  F: { label: 'Session failed', color: '#dc2626' },
};

function SessionDivider({ session }) {
  const status = SESSION_STATUS[session.status] || {
    label: 'New session',
    color: '#9ca3af',
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, margin: '12px 0' }}>
      <Box sx={{ flex: 1, borderBottom: '1px solid #e0e0e0' }} />
      <Typography
        variant="caption"
        sx={{ color: status.color, fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        {status.label} · {new Date(session.created_on).toLocaleString()}
      </Typography>
      <Box sx={{ flex: 1, borderBottom: '1px solid #e0e0e0' }} />
    </Box>
  );
}

// A true session's [created_on, ended_on] window never overlaps another session for the same contact, so
// this lookup is unambiguous - used to know which session (and therefore which divider) a given message
// belongs to, without a second network round trip.
function sessionForMessage(sessions, message) {
  if (!sessions || sessions.length === 0) return null;
  const t = new Date(message.created_on).getTime();
  return (
    sessions.find((s) => {
      const start = new Date(s.created_on).getTime();
      const end = s.ended_on ? new Date(s.ended_on).getTime() : Date.now();
      return t >= start && t <= end;
    }) || null
  );
}

export default function ChatTranscript({ messages, loading, maxHeight = 500, sessions }) {
  const containerRef = useRef(null);
  const lastMessageUuid = messages && messages.length > 0
    ? messages[messages.length - 1].uuid
    : null;

  // Messages render oldest-first (top) to newest-last (bottom), same convention as any chat app - but a
  // fixed-height scroll container defaults to scrollTop 0 on mount, hiding the newest message below the
  // fold until the user manually scrolls. Keyed on the last message's own uuid, not the messages array
  // reference, since live polling refetches (and returns a new array reference) every few seconds whether
  // or not anything new arrived - keying on the reference would yank a user back to the bottom mid-read
  // even when nothing changed.
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lastMessageUuid]);

  if (loading) {
    return <Typography variant="body2">Loading messages…</Typography>;
  }
  if (!messages || messages.length === 0) {
    return <Typography variant="body2">No messages found.</Typography>;
  }

  let lastSessionUuid = null;

  return (
    <Box
      ref={containerRef}
      sx={{
        padding: 2,
        maxHeight,
        overflowY: 'auto',
        border: '1px solid #eee',
        borderRadius: 1,
      }}
    >
      {messages.map((m) => {
        const session = sessionForMessage(sessions, m);
        const showDivider = session && session.uuid !== lastSessionUuid;
        lastSessionUuid = session ? session.uuid : lastSessionUuid;
        return (
          <React.Fragment key={m.uuid}>
            {showDivider && <SessionDivider session={session} />}
            <Bubble message={m} />
          </React.Fragment>
        );
      })}
    </Box>
  );
}
